import type { VoiceAiProvider } from '@pypestream/api-services/urql';

// Imported from the module, not the `utils` barrel, for the same reason as
// build-voice-ai-config.ts: the barrel drags in `api-services`, which this
// package's vitest setup stubs.
import { decodeFromBase64, encodeToBase64 } from '../../utils/encode-base64';

/**
 * One row of the Languages block (PSTUDIO-2950).
 *
 * `key` is the language (`es`) and `locale` is the ConversationRelay locale
 * (`es-US`). They are different things on the wire: the key is what the
 * solution sends as `active_language` and names the entry, the locale becomes
 * the entry's `code`. A channel holds at most one entry per key.
 *
 * `accent` only narrows the voice search. Engagement has no field for it, so
 * on load it comes back from the saved voice's own accent — which `withAccent`
 * keeps true, by dropping a voice that does not match a newly chosen accent.
 *
 * Speech recognition is deliberately absent: the provider and model are
 * channel-wide, and Engagement rejects the whole overlay if an entry carries
 * them.
 */
export type LanguageEntry = {
  key: string;
  locale: string;
  accent?: string;
  elevenlabsAudioModel: string;
  elevenlabsVoiceId: string;
  elevenlabsSpeed: number;
  elevenlabsStability: number;
  elevenlabsSimilarity: number;
  elevenlabsVoiceMetadata?: VoiceAiProvider;
};

export type LanguagesState = {
  startingLanguage: string;
  languages: LanguageEntry[];
};

/** The only voice provider Voice AI supports for the caller. */
export const LANGUAGE_TTS_PROVIDER = 'ElevenLabs';

export const DEFAULT_LOCALE = 'en-US';

/** The recommended ElevenLabs model, and the setup guide's default. */
export const DEFAULT_AUDIO_MODEL = 'flash_v2_5';

/** `es-US` → `es`. */
export const languageKeyOf = (locale: string): string =>
  locale.split('-')[0].toLowerCase();

type OverlayEntry = {
  code?: unknown;
  elevenlabs_voice_id?: unknown;
  elevenlabs_audio_model?: unknown;
  elevenlabs_speed?: unknown;
  elevenlabs_stability?: unknown;
  elevenlabs_similarity?: unknown;
  elevenlabs_voice_metadata?: unknown;
};

/**
 * Encodes the entries as `languages_config_b64`, the overlay Engagement's
 * `TwilioVoice.LanguagesConfig` reads.
 *
 * Returns `null` below two entries. A single language is exactly the legacy
 * flat config, so existing channels stay on the path they run today, and an
 * explicit null is what clears an overlay a channel no longer needs.
 *
 * The numbers are strings, as they are on the flat fields.
 */
export const encodeLanguagesConfig = (
  languages: LanguageEntry[],
  startingLanguage: string
): string | null => {
  if (languages.length < 2) return null;

  const overlay = {
    default_active_language: startingLanguage,
    languages: Object.fromEntries(
      languages.map((entry) => [
        entry.key,
        {
          code: entry.locale,
          tts_provider: LANGUAGE_TTS_PROVIDER,
          elevenlabs_voice_id: entry.elevenlabsVoiceId,
          elevenlabs_audio_model: entry.elevenlabsAudioModel,
          elevenlabs_speed: String(entry.elevenlabsSpeed),
          elevenlabs_stability: String(entry.elevenlabsStability),
          elevenlabs_similarity: String(entry.elevenlabsSimilarity),
          ...(entry.elevenlabsVoiceMetadata
            ? { elevenlabs_voice_metadata: entry.elevenlabsVoiceMetadata }
            : {}),
        },
      ])
    ),
  };

  return encodeToBase64(JSON.stringify(overlay));
};

/**
 * The patch for choosing an accent. A voice of a different accent is dropped:
 * left in place it would keep playing under the new accent's label, and the
 * accent could not be restored from it on load. Clearing the filter keeps it.
 */
export const withAccent = (
  entry: LanguageEntry,
  accent: string | undefined
): Partial<LanguageEntry> => {
  const voiceAccent = entry.elevenlabsVoiceMetadata?.accent;
  const mismatch =
    !!accent &&
    !!entry.elevenlabsVoiceId &&
    voiceAccent?.toLowerCase() !== accent.toLowerCase();
  return mismatch
    ? { accent, elevenlabsVoiceId: '', elevenlabsVoiceMetadata: undefined }
    : { accent };
};

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * A stored string, or '' when absent. The Channels API stores an absent value
 * as the STRING 'undefined' (PSTUDIO-4090), and 'null' appears the same way.
 */
const toStr = (value: unknown): string =>
  typeof value === 'string' && value !== 'undefined' && value !== 'null'
    ? value
    : '';

/** `es-us` → `es-US`: Engagement accepts any casing, the catalog uses this one. */
const normalizeLocale = (code: string): string => {
  const [language, region, ...rest] = code.split('-');
  return [
    language.toLowerCase(),
    ...(region ? [region.toUpperCase()] : []),
    ...rest,
  ].join('-');
};

const asMetadata = (value: unknown): VoiceAiProvider | undefined =>
  value && typeof value === 'object' ? (value as VoiceAiProvider) : undefined;

/**
 * Decodes `languages_config_b64`. Returns `null` for anything Engagement would
 * also refuse to use — absent, undecodable, no languages, or a default that is
 * not one of the keys — because in each of those cases the call runs on the
 * flat fields, and the editor has to show what will actually run.
 */
export const decodeLanguagesConfig = (
  b64: string | null | undefined
): LanguagesState | null => {
  if (!b64 || b64 === 'null') return null;

  let payload: unknown;
  try {
    payload = JSON.parse(decodeFromBase64(b64));
  } catch {
    return null;
  }
  if (!payload || typeof payload !== 'object') return null;

  const { default_active_language: starting, languages } = payload as {
    default_active_language?: unknown;
    languages?: unknown;
  };
  if (typeof starting !== 'string' || !starting) return null;
  if (!languages || typeof languages !== 'object') return null;

  const entries = Object.entries(languages as Record<string, OverlayEntry>);
  if (entries.length === 0) return null;
  if (!entries.some(([key]) => key === starting)) return null;

  return {
    startingLanguage: starting,
    languages: entries.map(([key, entry]) => {
      const metadata = asMetadata(entry?.elevenlabs_voice_metadata);
      return {
        key,
        locale: normalizeLocale(toStr(entry?.code)),
        accent: metadata?.accent || undefined,
        elevenlabsAudioModel: toStr(entry?.elevenlabs_audio_model),
        elevenlabsVoiceId: toStr(entry?.elevenlabs_voice_id),
        elevenlabsSpeed: toNumber(entry?.elevenlabs_speed),
        elevenlabsStability: toNumber(entry?.elevenlabs_stability),
        elevenlabsSimilarity: toNumber(entry?.elevenlabs_similarity),
        elevenlabsVoiceMetadata: metadata,
      };
    }),
  };
};

/** The subset of a channel's decoded `twilio_voice_config` this reads. */
export type TwilioLanguageFields = {
  language?: string | null;
  elevenlabs_audio_model?: string | null;
  elevenlabs_voice_id?: string | null;
  elevenlabs_speed?: number | string | null;
  elevenlabs_stability?: number | string | null;
  elevenlabs_similarity?: number | string | null;
  elevenlabs_voice_metadata?: VoiceAiProvider | null;
  languages_config_b64?: string | null;
};

/**
 * The Languages block's starting state for a saved channel: the overlay when
 * Engagement would use it, otherwise one entry built from the flat fields —
 * which is how every channel saved before PSTUDIO-2950 opens (PRD §7).
 */
export const languagesFromTwilioConfig = (
  config: TwilioLanguageFields
): LanguagesState => {
  const overlay = decodeLanguagesConfig(config.languages_config_b64);
  if (overlay) return overlay;

  const locale = normalizeLocale(toStr(config.language) || DEFAULT_LOCALE);
  const key = languageKeyOf(locale);
  const metadata = config.elevenlabs_voice_metadata || undefined;

  return {
    startingLanguage: key,
    languages: [
      {
        key,
        locale,
        accent: metadata?.accent || undefined,
        elevenlabsAudioModel: toStr(config.elevenlabs_audio_model),
        elevenlabsVoiceId: toStr(config.elevenlabs_voice_id),
        elevenlabsSpeed: toNumber(config.elevenlabs_speed),
        elevenlabsStability: toNumber(config.elevenlabs_stability),
        elevenlabsSimilarity: toNumber(config.elevenlabs_similarity),
        elevenlabsVoiceMetadata: metadata,
      },
    ],
  };
};
